import { useState } from "react";
import { motion } from "framer-motion";
import { Play, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useData } from "@/context/DataContext";
import { resolveUrl } from "@/lib/api";
import { Countdown } from "@/components/home/Countdown";
import { Dialog, DialogContent } from "@/components/ui/dialog";

export const Hero = () => {
  const { settings } = useData();
  const [playOpen, setPlayOpen] = useState(false);
  if (!settings) return <div className="h-screen bg-[#0B0C0E]" />;

  const video = settings.hero_video_url;

  return (
    <section data-testid="hero" className="relative min-h-[100svh] w-full overflow-hidden grain">
      {/* Background */}
      <div className="absolute inset-0">
        {video ? (
          <video src={resolveUrl(video)} autoPlay muted loop playsInline className="h-full w-full object-cover" />
        ) : (
          <img src={resolveUrl(settings.hero_image)} alt="Novemberkåsan" className="h-full w-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-[#0B0C0E]" />
        <div className="absolute inset-0 headlight-glow" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-5 sm:px-8 pb-16 pt-32">
        <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}>
          <div className="flex items-center gap-3 mb-5">
            <span className="h-px w-10 bg-amber-500" />
            <span className="font-mono text-xs uppercase tracking-[0.35em] text-amber-500">
              {settings.hero_location} · {settings.hero_date}
            </span>
          </div>
          <h1 className="font-display text-6xl sm:text-8xl lg:text-[10rem] font-black uppercase tracking-tight text-white leading-[0.9] text-glow">
            {settings.hero_title}
          </h1>
          <p className="mt-6 font-display text-2xl sm:text-4xl font-medium uppercase tracking-wide text-white/85">
            {settings.hero_tagline}
          </p>

          <div className="mt-9 flex flex-wrap gap-3">
            <Link to="/tavlingsinformation" data-testid="hero-info-btn" className="rounded-full bg-amber-500 px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-black hover:bg-amber-400 transition-colors amber-glow">
              Tävlingsinformation
            </Link>
            <Link to="/publik" data-testid="hero-publik-btn" className="rounded-full border border-white/25 bg-black/30 backdrop-blur px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-white hover:border-amber-500 hover:text-amber-500 transition-colors">
              För publik
            </Link>
            <a href={settings.anmalan_url || "#anmalan"} data-testid="hero-anmalan-btn" className="rounded-full border border-white/25 bg-black/30 backdrop-blur px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-white hover:border-amber-500 hover:text-amber-500 transition-colors">
              Anmälan
            </a>
            {video && (
              <button onClick={() => setPlayOpen(true)} data-testid="hero-play-btn" className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/30 backdrop-blur px-6 py-3.5 text-sm font-bold uppercase tracking-widest text-white hover:border-amber-500 hover:text-amber-500 transition-colors">
                <Play className="w-4 h-4 fill-current" /> Spela film
              </button>
            )}
          </div>

          <div className="mt-12">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 mb-3">Nedräkning till start</div>
            <Countdown />
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 text-white/40 animate-bounce">
        <ChevronDown className="w-6 h-6" />
      </div>

      {video && (
        <Dialog open={playOpen} onOpenChange={setPlayOpen}>
          <DialogContent className="max-w-4xl bg-black border-white/10 p-2">
            <video src={resolveUrl(video)} controls autoPlay className="w-full rounded-lg" />
          </DialogContent>
        </Dialog>
      )}
    </section>
  );
};
